/**
 * HeatingCharts V2 - Data Processing Utilities
 *
 * Utility functions for processing 15-minute interval heating data from V2 API.
 * These functions handle timeseries extraction, aggregation, formatting, and
 * responsive design calculations.
 */

import { format, parseISO } from 'date-fns';

/**
 * Extract timeseries from V2 API response and sort by timestamp
 *
 * @param {Object} data - V2 API response object
 * @returns {Array} Array of timeseries snapshots sorted by timestamp
 *
 * @example
 * const timeseries = extractTimeseries(v2Data);
 * // Returns: [{ timestamp: '2025-11-27 00:00:00', consumption: {...}, ... }, ...]
 */
export const extractTimeseries = (data) => {
  if (!data || !data.timeseries) {
    console.warn('extractTimeseries: No timeseries data found');
    return [];
  }

  try {
    const entries = Object.entries(data.timeseries)
      .sort(([timestampA], [timestampB]) => timestampA.localeCompare(timestampB))
      .map(([timestamp, snapshot]) => ({
        timestamp,
        ...snapshot
      }));

    console.log(`Extracted ${entries.length} timeseries entries`);
    return entries;
  } catch (error) {
    console.error('extractTimeseries: Error extracting timeseries', error);
    return [];
  }
};

/**
 * Aggregate 15-minute intervals to hourly intervals
 * Used for mobile view and overview charts
 *
 * @param {Array} timeseriesArray - Array of 15-min snapshots from extractTimeseries()
 * @returns {Array} Array of hourly aggregated snapshots
 *
 * @example
 * const hourly = aggregateToHourly(timeseries);
 * // 96 intervals → 24 hourly intervals
 */
export const aggregateToHourly = (timeseriesArray) => {
  if (!timeseriesArray || timeseriesArray.length === 0) {
    console.warn('aggregateToHourly: Empty timeseries array');
    return [];
  }

  try {
    const hourlyMap = {};

    timeseriesArray.forEach((snapshot) => {
      // Extract hour key (e.g., "2025-11-27 14:00:00")
      const hourKey = snapshot.timestamp.substring(0, 13) + ':00:00';

      if (!hourlyMap[hourKey]) {
        hourlyMap[hourKey] = {
          timestamp: hourKey,
          consumption: {
            total: 0,
            cost: 0,
            power_avg: 0,
            current: 0
          },
          pricing: null, // Use first interval's pricing (same for all 15-min in hour)
          outdoor_temp: null, // Use first interval's outdoor temp
          devices: {},
          metered: null,
          count: 0
        };
      }

      const hourData = hourlyMap[hourKey];

      // Aggregate consumption (sum)
      hourData.consumption.total += snapshot.consumption?.total || 0;
      hourData.consumption.cost += snapshot.consumption?.cost || 0;
      hourData.consumption.power_avg += snapshot.consumption?.power_avg || 0;
      hourData.consumption.current += snapshot.consumption?.current || 0;

      // Pricing: use hour_avg_price from the :00 snapshot (authoritative hourly avg from DynamoDB)
      // and carry day_avg/day_max/below_avg from first interval (same for all 15-min in hour)
      if (snapshot.pricing) {
        if (snapshot.pricing.hour_avg_price != null) {
          hourData.pricing = { ...snapshot.pricing, price: snapshot.pricing.hour_avg_price };
        } else if (!hourData.pricing) {
          hourData.pricing = { ...snapshot.pricing };
        }
      }

      // Store first interval's outdoor temp
      if (hourData.count === 0) {
        hourData.outdoor_temp = snapshot.outdoor_temp || null;
      }

      // Aggregate device data
      if (snapshot.devices) {
        Object.entries(snapshot.devices).forEach(([deviceId, deviceData]) => {
          if (!hourData.devices[deviceId]) {
            hourData.devices[deviceId] = {
              consumption: 0,
              power_avg: 0,
              current: 0,
              target_temp: null,
              measured_temp: null,
              reserve_heating: false,
              config: null,
              output_state: 0
            };
          }

          const hourDevice = hourData.devices[deviceId];

          // Sum consumption
          hourDevice.consumption += deviceData.consumption || 0;
          hourDevice.power_avg += deviceData.power_avg || 0;
          hourDevice.current += deviceData.current || 0;

          // Use first interval's config and temps
          if (hourData.count === 0) {
            hourDevice.target_temp = deviceData.target_temp;
            hourDevice.measured_temp = deviceData.measured_temp;
            hourDevice.config = deviceData.config;
          }

          // Logical OR for reserve_heating (true if any 15-min interval was true)
          hourDevice.reserve_heating = hourDevice.reserve_heating || deviceData.reserve_heating || false;

          // Max output_state (1 if any 15-min interval was ON)
          hourDevice.output_state = Math.max(hourDevice.output_state, deviceData.output_state || 0);
        });
      }

      // Aggregate metered data — nested structure matching backend:
      // metered.consumption, metered.grid, metered.solar, metered.power, metered.current
      if (snapshot.metered && !hourData.metered) {
        hourData.metered = {
          consumption: { total: 0, from_grid: 0, from_solar: 0, cost: 0 },
          grid: { import: 0, export: 0, import_cost: 0, export_income: 0 },
          solar: { production: 0, self_consumed: 0, exported: 0 },
          power: { avg: 0 },
          current: { total: 0, phase1: 0, phase2: 0, phase3: 0 }
        };
      }

      if (snapshot.metered && hourData.metered) {
        const sm = snapshot.metered;
        const hm = hourData.metered;
        if (sm.consumption) {
          hm.consumption.total += sm.consumption.total || 0;
          hm.consumption.from_grid += sm.consumption.from_grid || 0;
          hm.consumption.from_solar += sm.consumption.from_solar || 0;
          hm.consumption.cost += sm.consumption.cost || 0;
        }
        if (sm.grid) {
          hm.grid.import += sm.grid.import || 0;
          hm.grid.export += sm.grid.export || 0;
          hm.grid.import_cost += sm.grid.import_cost || 0;
          hm.grid.export_income += sm.grid.export_income || 0;
        }
        if (sm.solar) {
          hm.solar.production += sm.solar.production || 0;
          hm.solar.self_consumed += sm.solar.self_consumed || 0;
          hm.solar.exported += sm.solar.exported || 0;
        }
        // power.avg and current are instantaneous — accumulate for averaging
        if (sm.power) hm.power.avg += sm.power.avg || 0;
        if (sm.current) {
          hm.current.total += sm.current.total || 0;
          hm.current.phase1 += sm.current.phase1 || 0;
          hm.current.phase2 += sm.current.phase2 || 0;
          hm.current.phase3 += sm.current.phase3 || 0;
        }
      }

      // Aggregate root-level solar data (PV accounts use snapshot.solar directly)
      if (snapshot.solar && !hourData.solar) {
        hourData.solar = { production: 0, self_consumption: 0, export: 0, import: 0 };
      }
      if (snapshot.solar && hourData.solar) {
        hourData.solar.production += snapshot.solar.production || 0;
        hourData.solar.self_consumption += snapshot.solar.self_consumption || 0;
        hourData.solar.export += snapshot.solar.export || 0;
        hourData.solar.import += snapshot.solar.import || 0;
        // Carry instantaneous fields from last seen interval
        if (snapshot.solar.panel_power != null) hourData.solar.panel_power = snapshot.solar.panel_power;
        if (snapshot.solar.grid_power != null) hourData.solar.grid_power = snapshot.solar.grid_power;
      }

      hourData.count += 1;
    });

    // Convert map to array and calculate averages
    const hourlyArray = Object.values(hourlyMap).map(hourData => {
      const count = hourData.count;

      // Average power and current values
      hourData.consumption.power_avg = hourData.consumption.power_avg / count;
      hourData.consumption.current = hourData.consumption.current / count;

      // Average device power/current
      Object.values(hourData.devices).forEach(device => {
        device.power_avg = device.power_avg / count;
        device.current = device.current / count;
      });

      // Average metered instantaneous values (power and current — energy sums are left as-is)
      if (hourData.metered) {
        if (hourData.metered.power) hourData.metered.power.avg = hourData.metered.power.avg / count;
        if (hourData.metered.current) {
          hourData.metered.current.total = hourData.metered.current.total / count;
          hourData.metered.current.phase1 = hourData.metered.current.phase1 / count;
          hourData.metered.current.phase2 = hourData.metered.current.phase2 / count;
          hourData.metered.current.phase3 = hourData.metered.current.phase3 / count;
        }
      }

      delete hourData.count; // Remove count field
      return hourData;
    });

    console.log(`Aggregated ${timeseriesArray.length} intervals to ${hourlyArray.length} hourly intervals`);
    return hourlyArray;
  } catch (error) {
    console.error('aggregateToHourly: Error aggregating timeseries', error);
    return [];
  }
};

/**
 * Aggregate device-level data from 15-minute to hourly intervals
 * Used for mobile view in ThermostatChart and ConsumptionChart
 *
 * @param {Array} deviceDataArray - Array of device snapshots from extractDeviceData()
 * @returns {Array} Array of hourly aggregated device snapshots
 */
export const aggregateDeviceDataToHourly = (deviceDataArray) => {
  if (!deviceDataArray || deviceDataArray.length === 0) {
    console.warn('aggregateDeviceDataToHourly: Empty device data array');
    return [];
  }

  try {
    const hourlyMap = {};

    deviceDataArray.forEach((snapshot) => {
      // Extract hour key (e.g., "2025-11-27 14:00:00")
      const hourKey = snapshot.timestamp.substring(0, 13) + ':00:00';

      if (!hourlyMap[hourKey]) {
        hourlyMap[hourKey] = {
          timestamp: hourKey,
          consumption: 0,
          power_avg: 0,
          current: 0,
          target_temp: null,
          current_temp: null,
          measured_temp: null,
          reserve_heating: false,
          output_state: 0,
          override: 0,
          pv_output: 0,
          config: null,
          outdoor_temp: null,
          count: 0
        };
      }

      const hourData = hourlyMap[hourKey];

      // Sum consumption and power values
      hourData.consumption += snapshot.consumption || 0;
      hourData.power_avg += snapshot.power_avg || 0;
      hourData.current += snapshot.current || 0;

      // Use first interval's temperature and config values
      if (hourData.count === 0) {
        hourData.target_temp = snapshot.target_temp;
        hourData.current_temp = snapshot.current_temp;
        hourData.measured_temp = snapshot.measured_temp;
        hourData.config = snapshot.config;
        hourData.outdoor_temp = snapshot.outdoor_temp;
      }

      // Logical OR for boolean flags
      hourData.reserve_heating = hourData.reserve_heating || snapshot.reserve_heating || false;

      // Max for output states (1 if any 15-min interval was ON)
      hourData.output_state = Math.max(hourData.output_state, snapshot.output_state || 0);
      hourData.override = Math.max(hourData.override, snapshot.override || 0);
      hourData.pv_output = Math.max(hourData.pv_output, snapshot.pv_output || 0);

      hourData.count += 1;
    });

    // Convert map to array and calculate averages
    const hourlyArray = Object.values(hourlyMap).map(hourData => {
      const count = hourData.count;

      // Average power and current values
      hourData.power_avg = hourData.power_avg / count;
      hourData.current = hourData.current / count;

      delete hourData.count; // Remove count field
      return hourData;
    });

    console.log(`aggregateDeviceDataToHourly: Aggregated ${deviceDataArray.length} intervals to ${hourlyArray.length} hourly intervals`);
    return hourlyArray;
  } catch (error) {
    console.error('aggregateDeviceDataToHourly: Error aggregating device data', error);
    return [];
  }
};

/**
 * Get hourly tick timestamps for X-axis labels
 * Returns array of timestamps at hourly boundaries (00:00, 01:00, ..., 23:00)
 *
 * @param {Array} timeseriesArray - Array of snapshots
 * @returns {Array} Array of hourly timestamp strings
 *
 * @example
 * const ticks = getHourlyTicks(timeseries);
 * // Returns: ['2025-11-27 00:00:00', '2025-11-27 01:00:00', ...]
 */
export const getHourlyTicks = (timeseriesArray) => {
  if (!timeseriesArray || timeseriesArray.length === 0) {
    return [];
  }

  try {
    // Filter to only hourly timestamps (ending in :00:00)
    const hourlyTicks = timeseriesArray
      .filter(snapshot => snapshot.timestamp.endsWith(':00:00'))
      .map(snapshot => snapshot.timestamp);

    return hourlyTicks;
  } catch (error) {
    console.error('getHourlyTicks: Error extracting hourly ticks', error);
    return [];
  }
};

/**
 * Format timestamp for display
 *
 * @param {string} timestamp - Timestamp string from API (e.g., "2025-11-27 14:15:00")
 * @param {string} formatString - date-fns format string (default: "HH:mm")
 * @returns {string} Formatted timestamp
 *
 * @example
 * formatTimestamp('2025-11-27 14:15:00', 'HH:mm') // "14:15"
 * formatTimestamp('2025-11-27 14:15:00', 'HH') // "14"
 */
export const formatTimestamp = (timestamp, formatString = 'HH:mm') => {
  if (!timestamp) return '';

  try {
    // Parse timestamp string as local time (not UTC)
    const date = parseISO(timestamp.replace(' ', 'T'));
    return format(date, formatString);
  } catch (error) {
    console.error('formatTimestamp: Error formatting timestamp', timestamp, error);
    return timestamp;
  }
};

/**
 * Format timestamp for hourly X-axis labels
 * Returns just the hour (e.g., "14" for 14:00)
 *
 * @param {string} timestamp - Timestamp string from API
 * @returns {string} Formatted hour (e.g., "14")
 */
export const formatHourLabel = (timestamp) => {
  return formatTimestamp(timestamp, 'HH');
};

/**
 * Check if data contains metering information
 *
 * @param {Object} data - V2 API response object
 * @returns {boolean} True if metered data is present
 */
export const hasMetering = (data) => {
  if (!data || !data.timeseries) return false;

  try {
    // Check first several entries to see if any have metered data
    // (metered data may not be present in the very first entry)
    const snapshots = Object.values(data.timeseries).slice(0, 10);
    return snapshots.some(snapshot => snapshot && snapshot.metered);
  } catch (error) {
    console.error('hasMetering: Error checking metered data', error);
    return false;
  }
};

/**
 * Check if data contains PV (solar) information
 *
 * @param {Object} data - V2 API response object
 * @returns {boolean} True if PV data is present
 */
export const hasPV = (data) => {
  if (!data || !data.timeseries) return false;

  try {
    // Check if any snapshot has solar data (root-level 'solar' field used by PV accounts)
    // or metered.solar (used by accounts with full metering)
    return Object.values(data.timeseries).some(snapshot =>
      snapshot?.solar || (snapshot?.metered?.solar)
    );
  } catch (error) {
    console.error('hasPV: Error checking PV data', error);
    return false;
  }
};

/**
 * Extract device-specific data from timeseries
 * Returns array of snapshots with only the specified device's data
 *
 * @param {Array} timeseriesArray - Array of timeseries snapshots
 * @param {string} deviceId - Device ID to extract (e.g., "ilp_ak")
 * @returns {Array} Array of snapshots with device data
 *
 * @example
 * const deviceData = extractDeviceData(timeseries, 'ilp_ak');
 * // Returns: [{ timestamp: '...', consumption: 0.5, target_temp: 21, ... }, ...]
 */
export const extractDeviceData = (timeseriesArray, deviceId) => {
  if (!timeseriesArray || timeseriesArray.length === 0 || !deviceId) {
    return [];
  }

  try {
    return timeseriesArray
      .filter(snapshot => {
        // Include if device data exists OR if thermostat data exists (for future target temps)
        return (snapshot.devices && snapshot.devices[deviceId]) ||
               (snapshot.thermostats && snapshot.thermostats[deviceId]);
      })
      .map(snapshot => {
        const deviceData = snapshot.devices?.[deviceId];
        const thermostatData = snapshot.thermostats?.[deviceId];

        return {
          timestamp: snapshot.timestamp,
          consumption: deviceData?.consumption || 0,
          power_avg: deviceData?.power_avg || 0,
          current: deviceData?.current || 0,
          // Use target_temp from devices first, fall back to thermostats (for future data)
          target_temp: deviceData?.target_temp ?? thermostatData?.target_temp ?? null,
          current_temp: deviceData?.current_temp || null,
          measured_temp: deviceData?.measured_temp || null,
          reserve_heating: deviceData?.reserve_heating || false,
          output_state: deviceData?.output_state || deviceData?.output || 0,
          override: deviceData?.override || 0,
          pv_output: deviceData?.pv_output || 0,
          config: deviceData?.config || null,
          // For config devices, outdoor_temp may be in deviceData; fall back to snapshot level
          outdoor_temp: deviceData?.outdoor_temp ?? snapshot.outdoor_temp ?? null,
          // Config device specific fields (for #config devices)
          calibration: deviceData?.calibration ?? null,
          fan_level: deviceData?.fan_level ?? null
        };
      });
  } catch (error) {
    console.error('extractDeviceData: Error extracting device data', deviceId, error);
    return [];
  }
};

/**
 * Calculate responsive bar size based on container width
 *
 * @param {number} containerWidth - Container width in pixels
 * @param {number} dataPointCount - Number of bars to render (default: 96 for 15-min)
 * @returns {number} Bar size in pixels
 *
 * @example
 * getResponsiveBarSize(1200, 96) // ~10px per bar
 * getResponsiveBarSize(400, 96) // ~3px per bar
 */
export const getResponsiveBarSize = (containerWidth, dataPointCount = 96) => {
  if (!containerWidth || containerWidth <= 0) return 8; // Default

  // Calculate bar size based on available width
  const padding = 80; // Left/right axis padding
  const availableWidth = containerWidth - padding;
  const barSize = Math.floor(availableWidth / dataPointCount);

  // Clamp between 3px and 15px
  return Math.max(3, Math.min(15, barSize));
};

/**
 * Detect if mobile layout should be used
 *
 * @param {number} containerWidth - Container width in pixels
 * @returns {boolean} True if mobile layout (<768px)
 */
export const isMobileLayout = (containerWidth) => {
  return containerWidth < 768;
};

/**
 * Calculate average spot price for the day
 * Used to determine if consumption occurred during below/above average pricing
 *
 * @param {Array} timeseriesArray - Array of timeseries snapshots
 * @returns {number} Average spot price in the current unit (e.g., snt/kWh)
 */
export const calculateAverageSpotPrice = (timeseriesArray) => {
  if (!timeseriesArray || timeseriesArray.length === 0) {
    return 0;
  }

  try {
    const prices = timeseriesArray
      .map(snapshot => snapshot.pricing?.spot_price || 0)
      .filter(price => price > 0);

    if (prices.length === 0) return 0;

    const sum = prices.reduce((acc, price) => acc + price, 0);
    return sum / prices.length;
  } catch (error) {
    console.error('calculateAverageSpotPrice: Error calculating average', error);
    return 0;
  }
};

/**
 * Prepare data for SummaryChart_V2
 * Adds belowAvg flag for color coding and formats data for Recharts
 *
 * @param {Array} timeseriesArray - Array of timeseries snapshots
 * @param {number} avgPrice - Average spot price for the day
 * @returns {Array} Array formatted for Recharts with belowAvg flags
 */
export const prepareSummaryChartData = (timeseriesArray, avgPrice) => {
  if (!timeseriesArray || timeseriesArray.length === 0) {
    return [];
  }

  try {
    return timeseriesArray.map(snapshot => ({
      timestamp: snapshot.timestamp,
      time: formatTimestamp(snapshot.timestamp, 'HH:mm'),
      consumption: snapshot.consumption?.total || 0,
      meteredConsumption: snapshot.metered?.total || null,
      spotPrice: snapshot.pricing?.spot_price || 0,
      belowAvg: (snapshot.pricing?.spot_price || 0) < avgPrice
    }));
  } catch (error) {
    console.error('prepareSummaryChartData: Error preparing data', error);
    return [];
  }
};
